import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  Button,
  Alert,
  Collapse,
  Divider,
  Avatar,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Psychology as ReasoningIcon,
  Search as SearchIcon,
  Web as WebIcon,
  AutoAwesome as SynthesizeIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import StepTypeSelector from './StepTypeSelector';

const ACTION_ICONS = {
  RAG_SEARCH: <SearchIcon />,
  WEB_SEARCH: <WebIcon />,
  CORE_REASONING: <ReasoningIcon />,
  SYNTHESIZE: <SynthesizeIcon />
};

const ACTION_COLORS = {
  RAG_SEARCH: 'primary',
  WEB_SEARCH: 'info',
  CORE_REASONING: 'secondary',
  SYNTHESIZE: 'success'
};

const STATUS_ICONS = {
  pending: <ScheduleIcon />,
  running: <LinearProgress />,
  completed: <CheckIcon color="success" />,
  failed: <ErrorIcon color="error" />,
  skipped: <CancelIcon color="warning" />
};

const ReasoningPlanDisplay = ({
  plan,
  onPlanChange,
  onExecutePlan,
  executionStatus,
  isExecuting = false,
  showExecutionControls = true
}) => {
  const theme = useTheme();
  const [editingStep, setEditingStep] = useState(null);
  const [editedQuery, setEditedQuery] = useState('');
  const [editedAction, setEditedAction] = useState('');
  const [showDetails, setShowDetails] = useState({});
  const [showStepTypeSelector, setShowStepTypeSelector] = useState(false);
  const [availableStepTypes, setAvailableStepTypes] = useState([]);

  useEffect(() => {
    fetchStepTypes();
  }, []);

  const fetchStepTypes = async () => {
    try {
      const response = await fetch('/api/v1/plan/step-types');
      if (response.ok) {
        const types = await response.json();
        setAvailableStepTypes(types);
      }
    } catch (error) {
      console.error('Failed to fetch step types:', error);
    }
  };

  const handleEditStep = (stepIndex) => {
    setEditingStep(stepIndex);
    setEditedQuery(plan.steps[stepIndex].query);
    setEditedAction(plan.steps[stepIndex].action);
  };

  const handleSaveStep = (stepIndex) => {
    const updatedSteps = [...plan.steps];
    updatedSteps[stepIndex] = {
      ...updatedSteps[stepIndex],
      query: editedQuery,
      action: editedAction
    };

    onPlanChange({
      ...plan,
      steps: updatedSteps
    });

    setEditingStep(null);
    setEditedQuery('');
    setEditedAction('');
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setEditedQuery('');
    setEditedAction('');
  };

  const handleDeleteStep = (stepIndex) => {
    const updatedSteps = plan.steps.filter((_, index) => index !== stepIndex);
    // Renumber steps
    const renumberedSteps = updatedSteps.map((step, index) => ({
      ...step,
      step: index + 1
    }));
    
    onPlanChange({
      ...plan,
      steps: renumberedSteps
    });
  };

  const handleAddStep = () => {
    setShowStepTypeSelector(true);
  };

  const handleSelectStepType = (newStep) => {
    onPlanChange({
      ...plan,
      steps: [...plan.steps, newStep]
    });
  };

  const toggleStepDetails = (stepIndex) => {
    setShowDetails(prev => ({
      ...prev,
      [stepIndex]: !prev[stepIndex]
    }));
  };

  const getStepStatus = (stepNumber) => {
    if (!executionStatus || !executionStatus.step_results) return 'pending';
    
    const stepResult = executionStatus.step_results.find(r => r.step_number === stepNumber);
    return stepResult ? stepResult.status : 'pending';
  };

  const getStepResult = (stepNumber) => {
    if (!executionStatus || !executionStatus.step_results) return null;
    
    return executionStatus.step_results.find(r => r.step_number === stepNumber);
  };

  const renderStepContent = (step, stepIndex) => {
    const isEditing = editingStep === stepIndex;
    const stepStatus = getStepStatus(step.step);
    const stepResult = getStepResult(step.step);
    
    return (
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 2,
          border: stepStatus === 'running' ? `2px solid ${theme.palette.primary.main}` : undefined,
          backgroundColor: stepStatus === 'completed' ? 'rgba(76, 175, 80, 0.05)' : 
                          stepStatus === 'failed' ? 'rgba(244, 67, 54, 0.05)' : undefined
        }}
      >
        <CardContent>
          {/* Step Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                sx={{ 
                  bgcolor: `${ACTION_COLORS[step.action]}.main`,
                  width: 32,
                  height: 32
                }}
              >
                {ACTION_ICONS[step.action]}
              </Avatar>
              
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Step {step.step}: {step.action.replace('_', ' ')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {step.description}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Status indicator */}
              {stepStatus !== 'pending' && (
                <Chip
                  icon={STATUS_ICONS[stepStatus]}
                  label={stepStatus.toUpperCase()}
                  size="small"
                  color={stepStatus === 'completed' ? 'success' : 
                         stepStatus === 'failed' ? 'error' : 'default'}
                />
              )}
              
              {/* Action buttons */}
              {!isExecuting && (
                <>
                  <Tooltip title="Edit step">
                    <IconButton 
                      size="small" 
                      onClick={() => handleEditStep(stepIndex)}
                      disabled={isEditing}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete step">
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteStep(stepIndex)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              
              <Tooltip title="Show details">
                <IconButton 
                  size="small" 
                  onClick={() => toggleStepDetails(stepIndex)}
                >
                  <DragIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Step Query */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Query:
            </Typography>
            
            {isEditing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={editedAction}
                    onChange={(e) => setEditedAction(e.target.value)}
                    label="Action Type"
                  >
                    {availableStepTypes.map((stepType) => (
                      <MenuItem key={stepType.action} value={stepType.action}>
                        {stepType.name} ({stepType.action})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={editedQuery}
                    onChange={(e) => setEditedQuery(e.target.value)}
                    variant="outlined"
                    size="small"
                    label="Query/Prompt"
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleSaveStep(stepIndex)}
                    >
                      <SaveIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  p: 1.5,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  fontFamily: 'monospace'
                }}
              >
                {step.query}
              </Typography>
            )}
          </Box>
          
          {/* Step Details */}
          <Collapse in={showDetails[stepIndex]}>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Chip 
                label={`Action: ${step.action}`} 
                size="small" 
                color={ACTION_COLORS[step.action]}
              />
              {step.depends_on.length > 0 && (
                <Chip 
                  label={`Depends on: ${step.depends_on.join(', ')}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Box>
            
            {step.reasoning_context && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Reasoning Context:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1.5,
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.primary.light}`,
                    fontStyle: 'italic'
                  }}
                >
                  {step.reasoning_context}
                </Typography>
              </Box>
            )}

            {Object.keys(step.parameters).length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Parameters:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    p: 1,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem'
                  }}
                >
                  {JSON.stringify(step.parameters, null, 2)}
                </Typography>
              </Box>
            )}
          </Collapse>
          
          {/* Step Results */}
          {stepResult && stepResult.results && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Results:
              </Typography>
              
              {stepResult.output_summary && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  <MarkdownRenderer content={stepResult.output_summary} variant="body2" />
                </Alert>
              )}
              
              {stepResult.error_message && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {stepResult.error_message}
                </Alert>
              )}
              
              {stepResult.duration_seconds && (
                <Typography variant="caption" color="text.secondary">
                  Completed in {stepResult.duration_seconds.toFixed(2)} seconds
                </Typography>
              )}
            </Box>
          )}
          
          {/* Running indicator */}
          {stepStatus === 'running' && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
                Executing step...
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      {/* Plan Header */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Reasoning Plan
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {plan.original_query}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {plan.estimated_duration && (
                <Chip
                  icon={<ScheduleIcon />}
                  label={`~${Math.ceil(plan.estimated_duration / 60)} min`}
                  size="small"
                  variant="outlined"
                />
              )}
              {plan.complexity_score && (
                <Chip
                  label={`Complexity: ${plan.complexity_score.toFixed(1)}/10`}
                  size="small"
                  color={plan.complexity_score > 7 ? 'error' : plan.complexity_score > 4 ? 'warning' : 'success'}
                />
              )}
            </Box>
          </Box>

          {/* Reasoning Rationale */}
          {plan.reasoning_rationale && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(25, 118, 210, 0.04)', borderRadius: 1, border: 1, borderColor: 'primary.light' }}>
              <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                🧠 Reasoning Approach:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {plan.reasoning_rationale}
              </Typography>
            </Box>
          )}
          
          {/* Execution Controls */}
          {showExecutionControls && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={onExecutePlan}
                disabled={isExecuting || plan.steps.length === 0}
                sx={{ borderRadius: 2 }}
              >
                {isExecuting ? 'Executing...' : 'Run Plan'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddStep}
                disabled={isExecuting}
                sx={{ borderRadius: 2 }}
              >
                Add Step
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Plan Steps */}
      <Box>
        {plan.steps.map((step, index) => (
          <Box key={`${step.step}-${index}`}>
            {renderStepContent(step, index)}
          </Box>
        ))}
        
        {plan.steps.length === 0 && (
          <Alert severity="info">
            No steps in the plan. Click "Add Step" to get started.
          </Alert>
        )}
      </Box>
      
      {/* Final Synthesis Result */}
      {executionStatus && executionStatus.final_synthesis && (
        <Card variant="outlined" sx={{ mt: 3, border: `2px solid ${theme.palette.success.main}` }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom>
              🎯 Final Synthesis
            </Typography>
            <MarkdownRenderer
              content={executionStatus.final_synthesis}
              variant="body1"
              sx={{ lineHeight: 1.6 }}
            />
          </CardContent>
        </Card>
      )}

      {/* Step Type Selector Dialog */}
      <StepTypeSelector
        open={showStepTypeSelector}
        onClose={() => setShowStepTypeSelector(false)}
        onSelectStepType={handleSelectStepType}
        stepNumber={plan.steps.length + 1}
        availableStepTypes={availableStepTypes}
      />
    </Box>
  );
};

export default ReasoningPlanDisplay;
